import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  orderBy,
  query,
  deleteDoc,
  doc,
  updateDoc
} from "firebase/firestore";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../redux/loaderSlice";
import { calculateTenure, DEFAULT_GOLD_RATE_PER_GRAM, calculateInterest, calculateTotalPayable, calculateTotal, calculateOrnamentAmount, calculateTotalOrnamentNetGrams } from "../utils/calculations";
import { generateBillingInvoicePdf } from "../utils/pdfGenerator";

// ---------- Styled Components ----------
const Container = styled.div`
   margin-top: 60px;
  background: ${({ theme }) => theme.colors.background};
  min-height: 100vh;
  width: 100%;

  @media (max-width: 768px) {
    padding: 12px;
    overflow-x: hidden;
  }
`;

const Title = styled.h2`
  margin-bottom: 20px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 15px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.padding};
  border-radius: ${({ theme }) => theme.spacing.radius};
  box-shadow: ${({ theme }) => theme.shadow};
`;

const Row = styled.div`
  margin: 6px 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};

  span {
    font-weight: 600;
  }
`;

const Section = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  flex: 1;
  padding: 8px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;

  background: ${({ variant, theme }) =>
    variant === "delete"
      ? theme.colors.danger
      : variant === "edit"
        ? theme.colors.info
        : theme.colors.primary};

  color: white;
`;

// ---------- Component ----------
export default function Reports() {
  const [bills, setBills] = useState([]);
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.loader.loading);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  // ---------- FETCH ----------
  const fetchBills = async () => {
    dispatch(setLoading(true));
    try {
      const q = query(collection(db, "billing"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);

      setBills(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data()
        }))
      );
    } catch (err) {
      console.error(err);
      toast.error("Failed to load reports. You may not have permission to view billing data.");
    } finally {
      dispatch(setLoading(false));
    }
  };

  console.log("bills", bills);

  useEffect(() => {
    fetchBills();
  }, []);

  // ---------- DELETE ----------
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this bill?")) return;
    dispatch(setLoading(true));
    try {
      await deleteDoc(doc(db, "billing", id));
      toast.success("Bill deleted successfully");
      fetchBills();
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  // ---------- EDIT ----------
  const handleEdit = (bill) => {
    setEditingId(bill.id);
    setEditData(bill);
  };

  const handleUpdate = async () => {
    dispatch(setLoading(true));
    try {
      await updateDoc(doc(db, "billing", editingId), editData);
      toast.success("Bill updated successfully ✅");
      fetchBills();
      setEditingId(null);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  // ---------- EDIT HELPERS ----------
  const onlyNumbers = (v) => (v || "").toString().replace(/[^0-9]/g, "");

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
    });

  const updateEditField = (key, value) => {
    setEditData((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [key]: value,
      },
    }));
  };

  const handleDateChange = (type, val) => {
    updateEditField(type === "start" ? "startDate" : "endDate", val);
    // recalc tenure if both dates present
    setEditData((prev) => {
      const start = type === "start" ? val : prev.form?.startDate;
      const end = type === "end" ? val : prev.form?.endDate;
      const tenure = start && end ? calculateTenure(start, end) : prev.form?.tenure;
      return {
        ...prev,
        form: {
          ...prev.form,
          tenure,
        },
      };
    });
  };

  const updateOrnament = (index, key, value) => {
    setEditData((prev) => {
      const ornaments = Array.isArray(prev.ornaments) ? [...prev.ornaments] : [];
      ornaments[index] = { ...(ornaments[index] || {}), [key]: value };
      return { ...prev, ornaments };
    });
  };

  const handleGlobalOrnamentImage = async (file) => {
    if (!file) return;
    const base64 = await toBase64(file);
    setEditData((prev) => ({ ...prev, ornamentImage: base64 }));
  };

  // ---------- PDF ----------
  const handleGeneratePdf = (bill) => {
    dispatch(setLoading(true));

    try {
      const goldRate = Number(bill.form?.goldRate) || DEFAULT_GOLD_RATE_PER_GRAM;
      const interestAmount = calculateInterest(bill.form?.loanAmount, bill.form?.interest, bill.form?.tenure);
      const totalPayable = calculateTotalPayable(bill.form?.loanAmount, interestAmount);

      const ornamentsForPdf = (bill.ornaments || []).map((o) => {
        const net = o.net ?? 0;
        const gross = o.gross ?? 0;
        return {
          description: o.name || o.description || "",
          units: o.units ?? "",
          gross,
          net,
        };
      });

      const ornamentTotal = calculateTotal(ornamentsForPdf.map((o) => ({ ...o, net: o.net })), goldRate);

      const totalOrnamentNetGrams = calculateTotalOrnamentNetGrams(bill.ornaments || []);
      const ornamentCostFromTotalNet = calculateOrnamentAmount(totalOrnamentNetGrams, goldRate);

      const payload = {
        billingId: bill.customerId,
        branchName: bill.form?.bankName,
        customer: bill.form || {},
        loan: {
          ...bill.form,
          interestAmount,
          totalPayable,
          processingFeeAmount: (Number(bill.form?.loanAmount) || 0) * (Number(bill.form?.processingFeePercent) || 0) / 100,
          gstAmount: bill.form?.includeGst ? ((Number(bill.form?.loanAmount) || 0) * (Number(bill.form?.gstPercent) || 0) / 100) : 0,
          netLoanAmount: (Number(bill.form?.loanAmount) || 0) - ((Number(bill.form?.loanAmount) || 0) * (Number(bill.form?.processingFeePercent) || 0) / 100) - (bill.form?.includeGst ? ((Number(bill.form?.loanAmount) || 0) * (Number(bill.form?.gstPercent) || 0) / 100) : 0),
        },
        goldRate,
        ornamentImage: bill.ornamentImage || null,
        ornaments: ornamentsForPdf,
        totals: {
          ornamentTotal,
          totalPayable,
          totalOrnamentNetGrams,
          ornamentCostFromTotalNet,
        },
      };

      generateBillingInvoicePdf(payload);
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate PDF");
    } finally {
      dispatch(setLoading(false));
      toast.dismiss();
    }
  };

  if (loading) return <Container>Loading reports...</Container>;

  return (
    <Container>
      <Title>Saved Bills Reports</Title>

      <Grid>
        {bills.map((bill) => {
          const isEditing = editingId === bill.id;
          const currentForm = isEditing ? editData?.form : bill.form;
          const loanAmount = Number(currentForm?.loanAmount) || 0;
          const processingFeePercent = Number(currentForm?.processingFeePercent) || 0;
          const processingFeeAmount = (loanAmount * processingFeePercent) / 100;
          const includeGst = currentForm?.includeGst || false;
          const gstPercent = Number(currentForm?.gstPercent) || 0;
          const gstAmount = includeGst ? (loanAmount * gstPercent) / 100 : 0;
          const netLoanAmount = loanAmount - processingFeeAmount - gstAmount;

          return (
          <Card key={bill.id}>
            {/* BANK */}
            <Row>
              Bank:{" "}
              {isEditing ? (
                <input
                  value={editData?.form?.bankName || ""}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      form: {
                        ...editData.form,
                        bankName: e.target.value
                      }
                    })
                  }
                />
              ) : (
                <span>{bill.form?.bankName}</span>
              )}
            </Row>

            <Row>
              Customer ID: <span>{bill.customerId}</span>
            </Row>

            <Row>
              Name:{" "}
              {isEditing ? (
                <input
                  value={editData?.form?.customerName || ""}
                  onChange={(e) =>
                    updateEditField("customerName", e.target.value)
                  }
                />
              ) : (
                <span>{bill.form?.customerName}</span>
              )}
            </Row>

            <Row>
              Phone:{" "}
              {isEditing ? (
                <>
                  <span>+91 </span>
                  <input
                    value={editData?.form?.phone || ""}
                    onChange={(e) =>
                      updateEditField(
                        "phone",
                        onlyNumbers(e.target.value).slice(0, 10)
                      )
                    }
                  />
                </>
              ) : (
                <span>+91 {bill.form?.phone}</span>
              )}
            </Row>

            <Section>
              <Row>
                Loan:{" "}
                {isEditing ? (
                  <input
                    value={editData?.form?.loanAmount || ""}
                    onChange={(e) =>
                      updateEditField(
                        "loanAmount",
                        e.target.value.replace(/[^\d.]/g, "")
                      )
                    }
                  />
                ) : (
                  <span>₹{bill.form?.loanAmount}</span>
                )}
              </Row>
              <Row>
                Interest:{" "}
                {isEditing ? (
                  <input
                    value={editData?.form?.interest || ""}
                    onChange={(e) =>
                      updateEditField("interest", e.target.value.replace(/[^\d.]/g, ""))
                    }
                  />
                ) : (
                  <span>{bill.form?.interest}%</span>
                )}
              </Row>
              <Row>
                Processing Fee %:{" "}
                {isEditing ? (
                  <input
                    value={editData?.form?.processingFeePercent || ""}
                    onChange={(e) =>
                      updateEditField("processingFeePercent", e.target.value.replace(/[^\d.]/g, ""))
                    }
                  />
                ) : (
                  <span>{bill.form?.processingFeePercent || 0}%</span>
                )}
              </Row>
              {/* <Row>
                Processing Fee Amt: <span>₹{processingFeeAmount.toFixed(2)}</span>
              </Row> */}
              <Row>
                Include GST:{" "}
                {isEditing ? (
                  <input
                    type="checkbox"
                    checked={editData?.form?.includeGst || false}
                    onChange={(e) => updateEditField("includeGst", e.target.checked)}
                  />
                ) : (
                  <span>{bill.form?.includeGst ? "Yes" : "No"}</span>
                )}
              </Row>
              {currentForm?.includeGst && (
                <>
                  <Row>
                    GST %:{" "}
                    {isEditing ? (
                      <input
                        value={editData?.form?.gstPercent || ""}
                        onChange={(e) =>
                          updateEditField("gstPercent", e.target.value.replace(/[^\d.]/g, ""))
                        }
                      />
                    ) : (
                      <span>{bill.form?.gstPercent || 0}%</span>
                    )}
                  </Row>
                  {/* <Row>
                    GST Amt: <span>₹{gstAmount.toFixed(2)}</span>
                  </Row> */}
                </>
              )}
              <Row>
                Net Loan Amt: <span>₹{netLoanAmount.toFixed(2)}</span>
              </Row>
              <Row>
                Tenure:{" "}
                {isEditing ? (
                  <input value={editData?.form?.tenure || ""} readOnly />
                ) : (
                  <span>{bill.form?.tenure} days</span>
                )}
              </Row>
              {/* optional start/end dates */}
              <Row>
                Start:{" "}
                {isEditing ? (
                  <input
                    type="date"
                    value={editData?.form?.startDate || ""}
                    onChange={(e) => handleDateChange("start", e.target.value)}
                  />
                ) : (
                  <span>{bill.form?.startDate || "-"}</span>
                )}
              </Row>
              <Row>
                End:{" "}
                {isEditing ? (
                  <input
                    type="date"
                    value={editData?.form?.endDate || ""}
                    onChange={(e) => handleDateChange("end", e.target.value)}
                  />
                ) : (
                  <span>{bill.form?.endDate || "-"}</span>
                )}
              </Row>
            </Section>

            <Section>
              <strong>Ornaments</strong>
              
              <div style={{ marginBottom: 10, marginTop: 10 }}>
                {isEditing ? (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleGlobalOrnamentImage(e.target.files[0])
                      }
                      style={{ fontSize: 12, marginBottom: 8 }}
                    />
                    {editData?.ornamentImage && (
                      <div>
                        <img
                          src={editData.ornamentImage}
                          alt="ornaments"
                          width="60"
                          style={{ borderRadius: "6px", objectFit: "cover" }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  bill.ornamentImage && (
                    <img
                      src={bill.ornamentImage}
                      alt="ornaments"
                      width="60"
                      style={{ borderRadius: "6px", objectFit: "cover", display: 'block', marginBottom: 8 }}
                    />
                  )
                )}
              </div>

              {bill.ornaments?.map((o, i) => {
                const editOrn = editData?.ornaments?.[i] || {};
                return (
                  <Row key={i}>
                    {isEditing ? (
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <input
                          placeholder="name"
                          value={editOrn.name || editOrn.description || ""}
                          onChange={(e) => updateOrnament(i, "name", e.target.value)}
                        />
                        <input
                          placeholder="units"
                          value={editOrn.units || ""}
                          onChange={(e) => updateOrnament(i, "units", e.target.value.replace(/[^\d.]/g, ""))}
                          style={{ width: 60 }}
                        />
                        <input
                          placeholder="gross (g)"
                          value={editOrn.gross || ""}
                          onChange={(e) => updateOrnament(i, "gross", e.target.value.replace(/[^\d.]/g, ""))}
                          style={{ width: 80 }}
                        />
                        <input
                          placeholder="net (g)"
                          value={editOrn.net || ""}
                          onChange={(e) => updateOrnament(i, "net", e.target.value.replace(/[^\d.]/g, ""))}
                          style={{ width: 80 }}
                        />
                      </div>
                    ) : (
                      <>
                        {o.name || o.description} | {o.units} units | {o.gross || 0}g Gross | {o.net || 0}g Net
                      </>
                    )}
                  </Row>
                );
              })}
            </Section>

            <ButtonGroup>
              {isEditing ? (
                <Button onClick={handleUpdate}>Save</Button>
              ) : (
                <Button variant="edit" onClick={() => handleEdit(bill)}>
                  Edit
                </Button>
              )}
              <Button onClick={() => handleGeneratePdf(bill)}>
                Generate PDF
              </Button>

              <Button
                variant="delete"
                onClick={() => handleDelete(bill.id)}
              >
                Delete
              </Button>
            </ButtonGroup>
          </Card>
          );
        })}
      </Grid>
    </Container>
  );
}